import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IAddCaseFetchApiResponse,
  ICase,
  ICaseByIdFetchApiResponse,
  ICaseFilters,
  ICasesState,
  PatientCaseTypes,
  IVisit,
  IEditCaseVisits,
  ILastVisitResponse,
  ICaseVisitFetchApiResponse,
  IAddCaseVisits,
  IAllCaseFetchResponse,
  IAddVisitPayload,
  IAddCaseState,
} from "../interfaces/ICase";
import {
  fetchCaseById,
  fetchCases,
  fetchCaseTypes,
  updatePatienceCase,
  addVisit,
  editVisit,
  fetchCasesVisits,
  fetchCaseVisit,
  addPatientsCase,
} from "../apis/caseService";
import { resetPatientToCasesMap } from "./patientSlice";
import { fetchCreditNumbers } from "../apis/patientService";

const initialState: ICasesState = {
  cases: {},
  isFetchingAllCases: false,
  error: "",
  selectedCase: null,
  caseFilters: {
    endDate: "",
    orderBy: "DESC",
    pageNumber: 1,
    pageSize: 10,
    searchTerm: null,
    startDate: "",
  },
  caseToVisitsMap: {},
  pageToCaseIdMap: {},
  totalAvailableCases: 0,
  caseToCaseReferralsMap: {},
  caseType: null,
  fetchCaseDetails: null,
  isAddCase: false,
  errorMessage: null,
  isFetchingCaseVisits: false,
  selectedVisit: null,
  getCaseLastVisit: null,
  visits: {},
  caseToVisitMap: {},
  isLoading: false,
  creditNumber: [],
};

export const getCaseByCaseId = createAsyncThunk(
  "cases/getCaseByCaseId",
  async ({ id, accessToken }: { id: number; accessToken: string }) => {
    const response = await fetchCaseById(id);
    return response;
  }
);

export const getAllCases = createAsyncThunk(
  "cases/getAllCases",
  async ({
    filters,
    accessToken,
    hospitalId,
  }: {
    filters: ICaseFilters;
    accessToken: string;
    hospitalId: number | string | undefined;
  }) => {
    // handling 0 base indexing on server
    const pageNumber = filters.pageNumber > 0 ? filters.pageNumber - 1 : 0;
    const updatedFilters = { ...filters, pageNumber };
    const response = await fetchCases(updatedFilters, accessToken, hospitalId);
    return response;
  }
);

export const getCaseTypes = createAsyncThunk(
  "cases/getCaseTypes",
  async ({
    accessToken,
    schemeId,
  }: {
    accessToken: string;
    schemeId: number;
  }) => {
    const response = await fetchCaseTypes(accessToken, schemeId);
    return response;
  }
);

export const addPatientCase = createAsyncThunk(
  "cases/addPatientCase",
  async (
    {
      healthCareId,
      id,
      patientSchemeId,
      caseId,
      accessToken,
    }: {
      healthCareId: number;
      id: number;
      patientSchemeId: string;
      caseId: string;
      accessToken: string;
    },
    { dispatch, rejectWithValue }
  ) => {
    const response = await addPatientsCase(
      healthCareId,
      id,
      patientSchemeId,
      caseId
    );
    dispatch(resetPatientToCasesMap());
    return response;
  }
);
export const editPatientCase = createAsyncThunk(
  "cases/editPatientCase",
  async ({
    id,
    patientSchemeId,
    caseId,
  }: {
    id: number;
    patientSchemeId: string;
    caseId: string;
  }) => {
    const response = await updatePatienceCase(id, patientSchemeId, caseId);

    return response;
  }
);

export const getCaseVisits = createAsyncThunk(
  "cases/getCaseVisits",
  async (
    { caseId, accessToken }: { caseId: any; accessToken: string },
    thunkAPI
  ) => {
    const response = await fetchCasesVisits(caseId);
    return response;
  }
);
export const getCaseVisit = createAsyncThunk(
  "cases/getCaseVisit",
  async ({ caseId, accessToken }: { caseId: number; accessToken: string }) => {
    const response = await fetchCaseVisit(caseId);
    return response;
  }
);

export const addCaseVisit = createAsyncThunk(
  "cases/addVisit",
  async ({
    id,
    formValues,
    accessToken,
  }: {
    id: number;
    formValues: IAddVisitPayload;
    accessToken: string;
  }) => {
    const response = await addVisit(id, formValues);
    return response;
  }
);
export const editCaseVisit = createAsyncThunk(
  "visit/editCaseVisit",
  async ({
    id,
    visitId,
    formValues,
  }: {
    id: number;
    visitId: number;
    formValues: any;
  }) => {
    const response = await editVisit(id, visitId, formValues);
    return response;
  }
);

export const creditNumber = createAsyncThunk(
  "visit/credit_number",
  async (caseId: number) => {
    const response = await fetchCreditNumbers(caseId);
    return response;
  }
);

const casesSlice = createSlice({
  name: "cases",
  initialState: initialState,
  reducers: {
    updateSelectedCase: (state, action: PayloadAction<IAddCaseState>) => {
      state.selectedCase = action.payload;
    },
    setCaseFilters: (state, action: PayloadAction<ICaseFilters>) => {
      state.caseFilters = action.payload;
    },
    resetAddCaseState: (state) => {
      state.fetchCaseDetails = null;
      state.isAddCase = false;
      state.error = null;
    },
    clearPageToCaseIdMap: (state) => {
      state.pageToCaseIdMap = {};
    },
    handleVisitEdit: (state, action: PayloadAction<IVisit>) => {
      state.selectedVisit = action.payload;
    },
    resetFilteredVisits: (state) => {
      state.selectedVisit = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllCases.pending, (state) => {
        state.cases = {};
        state.pageToCaseIdMap = {};
        state.isFetchingAllCases = true;
      })
      .addCase(
        getAllCases.fulfilled,
        (state, action: PayloadAction<IAllCaseFetchResponse>) => {
          state.isFetchingAllCases = false;
          action.payload.casesResponseNewList?.forEach((aCase) => {
            if (aCase?.id) {
              state.cases[aCase.id] = aCase;

              if (!state.pageToCaseIdMap[state.caseFilters.pageNumber]) {
                state.pageToCaseIdMap[state.caseFilters.pageNumber] = [];
              }
              if (
                !state.pageToCaseIdMap[state.caseFilters.pageNumber].includes(
                  aCase.id
                )
              ) {
                state.pageToCaseIdMap[state.caseFilters.pageNumber].push(
                  aCase.id
                );
              }
            }
          });
          state.totalAvailableCases = action.payload.totalElements || 0;
          state.error = null;
          state.caseFilters.pageSize = 10;
        }
      )
      .addCase(getAllCases.rejected, (state, action) => {
        state.isFetchingAllCases = false;
        state.error = action.error.message || "Failed to fetch cases";
      })

      .addCase(getCaseByCaseId.pending, (state) => {
        state.error = null;
      })
      .addCase(
        getCaseByCaseId.fulfilled,
        (state, action: PayloadAction<ICaseByIdFetchApiResponse>) => {
          state.selectedCase = action.payload.caseResponse || null;
          if (
            action.payload.referralInfoResponseList &&
            action.payload.referralInfoResponseList.length > 0
          ) {
            state.caseToCaseReferralsMap[action.payload.caseId] =
              action.payload.referralInfoResponseList;
          }
        }
      )
      .addCase(getCaseByCaseId.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch case details";
      })
      .addCase(getCaseTypes.pending, (state) => {
        state.error = null;
      })
      .addCase(
        getCaseTypes.fulfilled,
        (state, action: PayloadAction<PatientCaseTypes>) => {
          state.caseType = action.payload.portalNames || null;
        }
      )
      .addCase(getCaseTypes.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch case types";
      })
      .addCase(addPatientCase.pending, (state) => {
        state.error = null;
        state.isAddCase = false;
        state.isLoading = true;
      })
      .addCase(
        addPatientCase.fulfilled,
        (state, action: PayloadAction<IAddCaseFetchApiResponse>) => {
          state.fetchCaseDetails = action.payload.fetchDetails || null;
          state.selectedCase = action.payload.fetchDetails || null;
          state.isAddCase = true;
          state.isLoading = false;
        }
      )
      .addCase(addPatientCase.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch case types";
        state.isLoading = false;
      })
      .addCase(editPatientCase.pending, (state) => {
        state.error = null;
        state.isLoading = true;
      })
      .addCase(
        editPatientCase.fulfilled,
        (state, action: PayloadAction<IAddCaseFetchApiResponse>) => {
          state.fetchCaseDetails = action.payload.fetchDetails || null;
          state.isLoading = false;
        }
      )
      .addCase(editPatientCase.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch case types";
        state.isLoading = false;
      })
      .addCase(addCaseVisit.pending, (state) => {
        state.error = "";
        state.isLoading = true;
      })
      .addCase(
        addCaseVisit.fulfilled,
        (state, action: PayloadAction<IAddCaseVisits>) => {
          state.visits = {};
          state.caseToVisitMap = {};
          state.isLoading = false;
        }
      )
      .addCase(addCaseVisit.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch case types";
        state.isLoading = false;
      })
      .addCase(getCaseVisits.pending, (state) => {
        state.isFetchingCaseVisits = true;
        state.error = "";
      })
      .addCase(
        getCaseVisits.fulfilled,
        (state, action: PayloadAction<ICaseVisitFetchApiResponse>) => {
          state.isFetchingCaseVisits = false;
          action.payload.visits?.forEach((visit: IVisit) => {
            state.visits[visit.id] = visit;
            const caseId = visit.caseId;

            const visitIndex = state.caseToVisitMap[caseId]?.findIndex(
              (existingVisit) => existingVisit?.id === visit?.id
            );

            if (!state.caseToVisitMap[caseId]) {
              state.caseToVisitMap[caseId] = [];
            }
            if (
              !state.caseToVisitMap[caseId]?.some(
                (existingVisit) => existingVisit.id === visit.id
              )
            ) {
              state.caseToVisitMap[caseId].push(visit);
            } else if (visitIndex !== -1) {
              state.caseToVisitMap[caseId][visitIndex] = visit;
            }
          });
        }
      )
      .addCase(getCaseVisits.rejected, (state, action) => {
        state.isFetchingCaseVisits = false;
        state.error = action.error.message || "Failed to fetch patient cases";
      })
      .addCase(editCaseVisit.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        editCaseVisit.fulfilled,
        (state, action: PayloadAction<IEditCaseVisits>) => {
          state.isLoading = false;
          state.visits = {};
          state.caseToVisitMap = {};
        }
      )
      .addCase(editCaseVisit.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch case types";
        state.isLoading = false;
      })
      .addCase(creditNumber.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(creditNumber.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.creditNumber = action.payload.schemeNames;
      })
      .addCase(creditNumber.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch case types";
        state.isLoading = false;
      })
      .addCase(getCaseVisit.pending, (state) => {
        state.error = "";
      })
      .addCase(
        getCaseVisit.fulfilled,
        (state, action: PayloadAction<ILastVisitResponse>) => {
          state.getCaseLastVisit = action.payload.getLastVisit;
        }
      )
      .addCase(getCaseVisit.rejected, (state, action) => {
        state.isFetchingCaseVisits = false;
        state.error = action.error.message || "Failed to fetch patient cases";
      });
  },
});

export const {
  updateSelectedCase,
  setCaseFilters,
  resetAddCaseState,
  resetFilteredVisits,
  handleVisitEdit,
  clearPageToCaseIdMap,
} = casesSlice.actions;
export default casesSlice.reducer;
